import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { X, AlertCircle } from "lucide-react";

export default function AddStudentForm({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    student_id: "",
    first_name: "",
    last_name: "",
    grade_level: "",
    email: "",
    f1g1_parent_phone: "",
    f1g2_parent_phone: "",
    f1g1_parent_email: "",
    f2g1_parent_phone: "",
    f2g1_parent_email: ""
  });
  const [error, setError] = useState(null);

  const queryClient = useQueryClient();

  const { data: existingStudents = [] } = useQuery({
    queryKey: ['students'],
    queryFn: () => base44.entities.Student.list(),
  });

  const createStudentMutation = useMutation({
    mutationFn: async (data) => {
      // Check for duplicate student_id
      const duplicate = existingStudents.find(
        s => s.student_id?.toLowerCase() === data.student_id.toLowerCase()
      );

      if (duplicate) {
        throw new Error(`Student ID "${data.student_id}" already exists for ${duplicate.first_name} ${duplicate.last_name}`);
      }

      return base44.entities.Student.create({
        student_id: data.student_id,
        first_name: data.first_name,
        last_name: data.last_name,
        grade_level: data.grade_level,
        email: data.email || undefined,
        f1g1_parent_phone: data.f1g1_parent_phone || undefined,
        f1g2_parent_phone: data.f1g2_parent_phone || undefined,
        f1g1_parent_email: data.f1g1_parent_email || undefined,
        f2g1_parent_phone: data.f2g1_parent_phone || undefined,
        f2g1_parent_email: data.f2g1_parent_email || undefined
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['students']);
      onSuccess();
    },
    onError: (err) => {
      setError(err.message || "Failed to add student");
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    
    if (!formData.student_id.trim()) {
      setError("Student ID is required");
      return;
    }
    
    createStudentMutation.mutate(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <Card className="w-full max-w-2xl my-8">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <CardTitle>Add New Student</CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="w-4 h-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="student_id">Student ID *</Label>
                <Input
                  id="student_id"
                  value={formData.student_id}
                  onChange={(e) => setFormData({...formData, student_id: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="grade_level">Grade Level</Label>
                <Input
                  id="grade_level"
                  value={formData.grade_level}
                  onChange={(e) => setFormData({...formData, grade_level: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email">Student Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
              />
            </div>

            <div className="pt-4 border-t">
              <h3 className="font-semibold text-slate-900 mb-3">Parent Contact Information</h3>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="f1g1_parent_phone">F1G1 Parent Phone</Label>
                    <Input
                      id="f1g1_parent_phone"
                      value={formData.f1g1_parent_phone}
                      onChange={(e) => setFormData({...formData, f1g1_parent_phone: e.target.value})}
                      placeholder="(XXX) XXX-XXXX"
                    />
                  </div>
                  <div>
                    <Label htmlFor="f1g1_parent_email">F1G1 Parent Email</Label>
                    <Input
                      id="f1g1_parent_email"
                      type="email"
                      value={formData.f1g1_parent_email}
                      onChange={(e) => setFormData({...formData, f1g1_parent_email: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="f1g2_parent_phone">F1G2 Parent Phone #2</Label>
                    <Input
                      id="f1g2_parent_phone"
                      value={formData.f1g2_parent_phone}
                      onChange={(e) => setFormData({...formData, f1g2_parent_phone: e.target.value})}
                      placeholder="(XXX) XXX-XXXX"
                    />
                  </div>
                  <div>
                    <Label htmlFor="f2g1_parent_phone">F2G1 Parent Phone</Label>
                    <Input
                      id="f2g1_parent_phone"
                      value={formData.f2g1_parent_phone}
                      onChange={(e) => setFormData({...formData, f2g1_parent_phone: e.target.value})}
                      placeholder="(XXX) XXX-XXXX"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="f2g1_parent_email">F2G1 Parent Email</Label>
                  <Input
                    id="f2g1_parent_email"
                    type="email"
                    value={formData.f2g1_parent_email}
                    onChange={(e) => setFormData({...formData, f2g1_parent_email: e.target.value})}
                  />
                </div>
              </div>
            </div>

            <Button type="submit" className="w-full bg-yellow-600 hover:bg-yellow-700" disabled={createStudentMutation.isPending}>
              {createStudentMutation.isPending ? "Adding..." : "Add Student"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}